// src/pages/admin/Login.js
import React, { useState } from "react";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  sendPasswordResetEmail,
  signOut,
} from "firebase/auth";
import { auth } from "../../firebase";
import { db } from "../../firebase";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate, Link } from "react-router-dom";

export default function Login() {
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", remember: true });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const niceError = (e) => {
    const code = e?.code || "";
    if (code.includes("invalid-email")) return "Adresse email invalide.";
    if (code.includes("invalid-credential") || code.includes("wrong-password"))
      return "Identifiants incorrects. Vérifiez l'email ou le mot de passe.";
    if (code.includes("user-not-found")) return "Aucun utilisateur trouvé avec cet email.";
    if (code.includes("too-many-requests")) return "Trop de tentatives. Réessayez plus tard.";
    if (code.includes("network-request-failed")) return "Problème réseau. Vérifiez votre connexion internet.";
    return e?.message || "Une erreur est survenue.";
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setInfo("");
    setLoading(true);
    try {
      await setPersistence(auth, form.remember ? browserLocalPersistence : browserSessionPersistence);
      const cred = await signInWithEmailAndPassword(auth, form.email.trim(), form.password);

      // Vérifier l'autorisation admin (doc admins/{uid} role===admin)
      const adminRef = doc(db, "admins", cred.user.uid);
      const adminSnap = await getDoc(adminRef);
      const isAdmin = adminSnap.exists() && adminSnap.data()?.role === "admin";
      if (!isAdmin) {
        await signOut(auth);
        setErr("Ce compte n'est pas autorisé à accéder à l'administration.");
        setLoading(false);
        return;
      }

      // ok → vers /admin
      nav("/admin", { replace: true });
    } catch (e2) {
      setErr(niceError(e2));
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    setErr("");
    setInfo("");
    if (!form.email.trim()) {
      setErr("Renseignez votre email pour recevoir le lien de réinitialisation.");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, form.email.trim());
      setInfo("Email de réinitialisation envoyé. Vérifiez votre boîte de réception.");
    } catch (e) {
      setErr(niceError(e));
    }
  };

  return (
    <div className="container py-5" style={{ maxWidth: 420 }}>
      <h2 className="fw-bold mb-3">Connexion Admin</h2>
      <p className="text-muted small mb-4">
        Accès réservé aux comptes autorisés. <br />
        Besoin d’un accès ? Contactez l’administrateur principal.
      </p>

      {err && <div className="alert alert-danger">{err}</div>}
      {info && <div className="alert alert-success">{info}</div>}

      <form onSubmit={submit} className="card card-body shadow-sm">
        <div className="mb-3">
          <label className="form-label">Email</label>
          <input
            name="email"
            type="email"
            className="form-control"
            value={form.email}
            onChange={onChange}
            placeholder="admin@mameche.tg"
            required
            autoFocus
          />
        </div>

        <div className="mb-3">
          <label className="form-label d-flex justify-content-between">
            <span>Mot de passe</span>
            <button
              type="button"
              className="btn btn-link p-0 small"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            >
              {showPw ? "Masquer" : "Afficher"}
            </button>
          </label>
          <input
            name="password"
            type={showPw ? "text" : "password"}
            className="form-control"
            value={form.password}
            onChange={onChange}
            placeholder="••••••••"
            required
          />
        </div>

        <div className="form-check mb-3">
          <input
            className="form-check-input"
            type="checkbox"
            id="remember"
            name="remember"
            checked={form.remember}
            onChange={onChange}
          />
          <label className="form-check-label" htmlFor="remember">
            Se souvenir de moi
          </label>
        </div>

        <div className="d-grid">
          <button className="btn btn-success" disabled={loading}>
            {loading ? "Connexion…" : "Se connecter"}
          </button>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <button type="button" className="btn btn-link p-0 small" onClick={resetPassword}>
            Mot de passe oublié ?
          </button>
          <Link to="/" className="small text-decoration-none">
            ← Retour au site
          </Link>
        </div>
      </form>

      <div className="text-muted small mt-3">
        Astuce : pour autoriser un compte, créez <code>admins/&lt;UID&gt;</code> avec <code>{`{ role: "admin" }`}</code> dans
        Firestore.
      </div>
    </div>
  );
}
